import React, { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { UPDATE_CONTACTS_MUTATION } from 'Queries/Mutations';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { CustomModal } from 'components/CustomModal';
import { ErrorBanner } from 'components/ErrorBanner/ErrorBanner';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormField } from 'components/FormHelper/FormField';
import { HintText } from 'components/HintText/HintText';

import { VALIDATION_CONSTANTS } from 'global/constants';

import './Popup.sass';

export const DebInstallerEmailPopup = ({ mainFormik, popupOpen, setPopupOpen, onSaved }) => {
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [apiError, setApiError] = useState(null);

  const currentEmail = mainFormik.values.email || '';
  const normalizedCurrentEmail = currentEmail.trim().toLowerCase();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        newEmail: Yup.string()
          .email(VALIDATION_CONSTANTS.INVALID_EMAIL)
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .test(
            'new-email-different-from-current',
            'Die neue E-Mail-Adresse muss von der aktuellen E-Mail-Adresse abweichen',
            (val) => {
              if (!val) return true;
              return val.trim().toLowerCase() !== normalizedCurrentEmail;
            }
          ),
        confirmEmail: Yup.string()
          .required('Bitte bestätigen Sie die neue E-Mail-Adresse')
          .oneOf([Yup.ref('newEmail')], 'Die E-Mail-Adressen stimmen nicht überein'),
      }),
    [normalizedCurrentEmail]
  );

  const formik = useFormik({
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      newEmail: '',
      confirmEmail: '',
    },
  });

  const [updateContactsMutation, { loading: isSaving }] = useMutation(UPDATE_CONTACTS_MUTATION);

  useEffect(() => {
    if (!popupOpen) return;
    formik.resetForm();
    setUpdateSuccess(false);
    setApiError(null);
  }, [popupOpen]);

  const onCancel = () => {
    if (updateSuccess) {
      mainFormik.setFieldValue('email', formik.values.newEmail);
      onSaved?.({ email: formik.values.newEmail });
    }
    formik.resetForm();
    setUpdateSuccess(false);
    setApiError(null);
    setPopupOpen(false);
  };

  const onSubmit = () => {
    if (!formik.isValid) return;

    updateContactsMutation({
      variables: {
        input: {
          email: formik.values.newEmail,
        },
      },
    })
      .then(({ data }) => {
        if ([409, 400].includes(data.updateInstallerExpertTelephoneEmail.status)) {
          setApiError('Die E-Mail-Adresse ist bereits vergeben');
        } else {
          setUpdateSuccess(true);
        }
      })
      .catch(() => {
        setApiError(
          'Beim Aktualisieren der Daten ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.'
        );
      });
  };

  const modalContent = () => {
    if (updateSuccess) {
      return (
        <div className="popup-content">
          Vielen Dank. Ihre Kontaktdaten wurden gespeichert. <br />
          An die neue E-Mail-Adresse <strong>{formik.values.newEmail}</strong> wurde ein
          Bestätigungslink versendet. Bis die neue E-Mail-Adresse bestätigt wurde, erfolgt die
          Anmeldung weiterhin mit Ihrer bisherigen E-Mail-Adresse. Sollten Probleme auftreten,
          wenden Sie sich bitte an Ihren Netzbetreiber.
        </div>
      );
    }

    if (apiError) {
      return (
        <div className="popup-content">
          <ErrorBanner>{apiError}</ErrorBanner>
        </div>
      );
    }

    return (
      <div className="popup-content">
        <div className="popup-content__popup-field">
          <FormField
            name="currentEmail"
            label="Aktuelle E-Mail-Adresse"
            value={currentEmail}
            oldValue=""
            disabled
            fullWidth
          />
        </div>
        <HintText style={{ justifyContent: 'center' }}>
          Mit der Änderung der E-Mail-Adresse ändern Sie die Zugangsdaten zum mAP und e-fix.
        </HintText>
        <div className="popup-content__popup-field">
          <FormField
            name="newEmail"
            formik={formik}
            label="Neue E-Mail-Adresse"
            oldValue=""
            fullWidth
          />
        </div>
        <div className="popup-content__popup-field">
          <FormField
            name="confirmEmail"
            formik={formik}
            label="Neue E-Mail-Adresse bestätigen"
            oldValue=""
            fullWidth
          />
        </div>
      </div>
    );
  };

  const modalButtons = () => {
    if (updateSuccess || apiError) {
      return <ActionButton inverted onClick={onCancel} title="OK" />;
    }

    return (
      <FormActionButtons
        hasErrors={Object.keys(formik.errors).length > 0}
        titleLeft="Abbrechen"
        titleRight="Speichern"
        onClickLeft={onCancel}
        onClickRight={onSubmit}
        loading={isSaving}
        errorMessage={formik.errors.newEmail || formik.errors.confirmEmail}
      />
    );
  };

  return (
    <CustomModal
      isOpen={popupOpen}
      title="E-Mail-Adresse bearbeiten"
      content={modalContent()}
      closeCancelEventModal={onCancel}
      ctaButtons={modalButtons()}
    />
  );
};

DebInstallerEmailPopup.displayName = 'DebInstallerEmailPopup';

DebInstallerEmailPopup.propTypes = {
  mainFormik: PropTypes.object.isRequired,
  popupOpen: PropTypes.bool,
  setPopupOpen: PropTypes.func,
  onSaved: PropTypes.func,
};

DebInstallerEmailPopup.defaultProps = {
  popupOpen: false,
  setPopupOpen: () => {},
  onSaved: undefined,
};
